/**
 * Aspect Ratio Detection Utilities
 * US1835672: Support for multiple aspect ratios in SRP gallery
 *
 * Detects and categorizes image aspect ratios from loaded images
 */

/**
 * Common aspect ratio categories with tolerance
 */
export const ASPECT_RATIO_CATEGORIES = {
	SQUARE: { ratio: 1, name: '1:1 (Square)', tolerance: 0.05 },
	STANDARD: { ratio: 4 / 3, name: '4:3 (Standard)', tolerance: 0.05 },
	WIDESCREEN: { ratio: 16 / 9, name: '16:9 (Widescreen)', tolerance: 0.05 },
	PANORAMIC: { ratio: 2.35, name: '21:9 (Panoramic)', tolerance: 0.1 },
	PORTRAIT: { ratio: 3 / 4, name: '3:4 (Portrait)', tolerance: 0.05 }
};

/**
 * Categorize aspect ratio into common types
 * @param {number} ratio - The calculated aspect ratio
 * @returns {string} Category name
 */
export const categorizeAspectRatio = (ratio) => {
	const categories = Object.entries(ASPECT_RATIO_CATEGORIES);
	const matchedCategory = categories.find(
		([, value]) => Math.abs(ratio - value.ratio) <= value.tolerance
	);

	if (matchedCategory) {
		return matchedCategory[0];
	}

	// Determine if custom landscape or portrait
	if (ratio > 1.2) return 'CUSTOM_LANDSCAPE';
	if (ratio < 0.8) return 'CUSTOM_PORTRAIT';

	return 'CUSTOM_SQUARE';
};

/**
 * Format aspect ratio for CSS aspect-ratio property
 * @param {number} width - Image width
 * @param {number} height - Image height
 * @returns {string} CSS aspect-ratio value (e.g., "16 / 9")
 */
export const formatCssAspectRatio = (width, height) => {
	if (!width || !height) return null;

	// Simplify common ratios for cleaner CSS
	const ratio = width / height;
	const roundedRatio = Math.round(ratio * 100) / 100;

	// Check for common ratios
	if (Math.abs(ratio - 4 / 3) < 0.01) return '4 / 3';
	if (Math.abs(ratio - 16 / 9) < 0.01) return '16 / 9';
	if (Math.abs(ratio - 3 / 4) < 0.01) return '3 / 4';
	if (Math.abs(ratio - 1) < 0.01) return '1 / 1';

	// For other ratios, use actual dimensions (simplified by GCD if possible)
	const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
	const divisor = gcd(width, height);
	const simplifiedWidth = width / divisor;
	const simplifiedHeight = height / divisor;

	// If simplified ratio is reasonable, use it
	if (simplifiedWidth < 100 && simplifiedHeight < 100) {
		return `${simplifiedWidth} / ${simplifiedHeight}`;
	}

	// Otherwise use decimal
	return `${roundedRatio.toFixed(2)}`;
};

/**
 * Calculate aspect ratio from image element
 * @param {HTMLImageElement} imageElement - The loaded image element
 * @returns {Object} Aspect ratio data
 */
export const calculateAspectRatio = (imageElement) => {
	if (
		!imageElement ||
		!imageElement.naturalWidth ||
		!imageElement.naturalHeight
	) {
		return {
			ratio: null,
			width: null,
			height: null,
			category: 'UNKNOWN',
			cssRatio: null
		};
	}

	const width = imageElement.naturalWidth;
	const height = imageElement.naturalHeight;
	const ratio = width / height;

	return {
		ratio: parseFloat(ratio.toFixed(3)),
		width,
		height,
		category: categorizeAspectRatio(ratio),
		cssRatio: formatCssAspectRatio(width, height)
	};
};

/**
 * Get aspect ratio from image ref (for use in onLoad handlers)
 * @param {Object} imageRef - React ref object containing image element
 * @returns {Object} Aspect ratio data
 */
export const getAspectRatioFromRef = (imageRef) => {
	if (!imageRef || !imageRef.current) {
		return calculateAspectRatio(null);
	}
	return calculateAspectRatio(imageRef.current);
};

/**
 * Determine if aspect ratio should trigger special handling
 * @param {number} ratio - The calculated aspect ratio
 * @returns {Object} Handling recommendations
 */
export const getAspectRatioHandling = (ratio) => {
	if (!ratio) {
		return { useDefault: true, strategy: 'default' };
	}

	const category = categorizeAspectRatio(ratio);

	// Portrait images (out of scope) - crop them
	if (category === 'PORTRAIT' || category === 'CUSTOM_PORTRAIT') {
		return {
			useDefault: false,
			strategy: 'crop',
			reasoning:
				'Portrait images out of scope - crop to prevent layout issues'
		};
	}

	// Standard 4:3 - use default behavior
	if (category === 'STANDARD') {
		return {
			useDefault: true,
			strategy: 'default',
			reasoning: 'Standard 4:3 ratio'
		};
	}

	// Widescreen 16:9 - needs dynamic height or crop
	if (category === 'WIDESCREEN') {
		return {
			useDefault: false,
			strategy: 'dynamic',
			reasoning: 'Widescreen 16:9 - allow natural aspect ratio'
		};
	}

	// Panoramic images - may need max-height constraint
	if (category === 'PANORAMIC') {
		return {
			useDefault: false,
			strategy: 'dynamic-constrained',
			reasoning: 'Panoramic - allow natural ratio with max-height'
		};
	}

	return {
		useDefault: false,
		strategy: 'dynamic',
		reasoning: 'Non-standard ratio'
	};
};

export default {
	calculateAspectRatio,
	categorizeAspectRatio,
	formatCssAspectRatio,
	getAspectRatioFromRef,
	getAspectRatioHandling,
	ASPECT_RATIO_CATEGORIES
};
